import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import "./ui.css";

/**
 * Modal + useConfirm – náhrada window.confirm/alert dialogů.
 *
 * Modal: obecné dialogové okno s Bootstrap markupem (.modal.show + backdrop)
 * řízené React stavem (bez bootstrap.js). Esc i klik na pozadí zavírá, fokus
 * se drží uvnitř (ruční past – cílový Chrome 80 WebView nezná atribut inert)
 * a po zavření se vrací na prvek, který dialog otevřel. Scroll hlavního
 * obsahu se po dobu otevření zamyká přes data-lks-modal-open na <html>
 * (scroll kontejner je .lks-main, ne body).
 *
 * useConfirm: const confirm = useConfirm();
 *   if (!(await confirm({ title: "Smazat závod?", message: ..., tone: "danger" }))) return;
 * Destruktivní akce mohou vyžadovat opsání slova (requireText).
 * ConfirmProvider žije v kořeni App.tsx vedle ToastProvideru.
 */

// ── Zámek scrollu hlavního obsahu ────────────────────────────────
let openModals = 0;

function lockScroll() {
  openModals += 1;
  document.documentElement.setAttribute("data-lks-modal-open", "true");
}

function unlockScroll() {
  openModals = Math.max(0, openModals - 1);
  if (openModals === 0) {
    document.documentElement.removeAttribute("data-lks-modal-open");
  }
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ── Obecný Modal ─────────────────────────────────────────────────

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Obsah patičky (tlačítka). Bez něj se patička nevykreslí. */
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  /** Dlouhý obsah: scroluje tělo dialogu, ne stránka (modal-dialog-scrollable). */
  scrollBody?: boolean;
  /** Prvek, který dostane fokus po otevření (jinak první fokusovatelný). */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  scrollBody = false,
  initialFocusRef,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`lks-modal-title-${Math.floor(performance.now() * 1000) % 1000000}`);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Zámek scrollu + návrat fokusu po zavření.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    lockScroll();
    return () => {
      unlockScroll();
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === "function") {
        try {
          el.focus();
        } catch {
          /* prvek mohl mezitím zmizet */
        }
      }
    };
  }, [open]);

  // Výchozí fokus dovnitř dialogu.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const target =
      initialFocusRef?.current ??
      dialog.querySelector<HTMLElement>("[data-lks-autofocus]") ??
      dialog.querySelector<HTMLElement>(FOCUSABLE);
    if (target) target.focus();
  }, [open, initialFocusRef]);

  // Escape a fokus past posloucháme na document: React onKeyDown na dialogu
  // by nedostal událost, když fokus spadne na body (klik do textu zprávy).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE)
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) {
        // Fokus utekl za backdrop (nebo na body) – vrátit do dialogu.
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass =
    size === "sm" ? " modal-sm" : size === "lg" ? " modal-lg" : "";

  // Portál do body: uvnitř .lks-main (vlastní stacking context, z-index 0)
  // by dialog i jeho pozadí překryla fixní hlavička a spodní navigace.
  return createPortal(
    <>
      <div className="modal-backdrop show" onClick={onClose} />
      <div
        className="modal show d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        onMouseDown={(e) => {
          // Klik mimo dialog (na plochu .modal) zavírá; mousedown kvůli tomu,
          // aby tažení textu z dialogu ven nezavřelo okno.
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className={`modal-dialog modal-dialog-centered${sizeClass}${
            scrollBody ? " modal-dialog-scrollable" : ""
          }`}
        >
          {/* tabIndex -1: klik do těla dialogu nepustí fokus na body */}
          <div className="modal-content lks-modal" ref={dialogRef} tabIndex={-1}>
            <div className="modal-header lks-modal__header">
              <h2 className="modal-title lks-modal__title" id={titleId.current}>
                {title}
              </h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Zavřít"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">{children}</div>
            {footer ? (
              <div className="modal-footer lks-modal__footer">{footer}</div>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ── useConfirm ───────────────────────────────────────────────────

export interface ConfirmOptions {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = červené potvrzení pro destruktivní akce. */
  tone?: "default" | "danger";
  /**
   * Pojistka destruktivních akcí: uživatel musí slovo opsat
   * (např. requireText="SMAZAT"), jinak je potvrzení neaktivní.
   */
  requireText?: string;
  /** Doplňkový obsah – např. výčet dopadů akce. */
  details?: React.ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
  /** location.key v okamžiku otevření – navigační efekt ruší jen cizí dialogy. */
  locationKey: string;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [typed, setTyped] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Navigace (hardwarové Zpět) otevřený dialog zavírá jako „Zrušit" – jinak by
  // přežil nad novou obrazovkou a potvrzení by resolvlo do odmountované.
  // Ruší se jen dialog otevřený pod JINÝM location.key: confirm() zavolaný
  // z mount efektu čerstvě otevřené obrazovky musí přežít (efekt provideru
  // běží až po efektech potomků, viděl by ho jako „starý").
  const locationKey = location.key;
  const locationKeyRef = useRef(locationKey);
  locationKeyRef.current = locationKey;
  useEffect(() => {
    setPending((current) => {
      if (!current || current.locationKey === locationKey) return current;
      current.resolve(false);
      return null;
    });
  }, [locationKey]);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setTyped("");
      setPending((current) => {
        // Dva souběžné dotazy nemají jak nastat (dialog je modální);
        // kdyby přece, starší odpovíme „ne", ať nikde nevisí promise.
        if (current) current.resolve(false);
        return { options, resolve, locationKey: locationKeyRef.current };
      });
    });
  }, []);

  const close = useCallback(
    (result: boolean) => {
      setPending((current) => {
        if (current) current.resolve(result);
        return null;
      });
    },
    []
  );

  const options = pending?.options;
  const requireOk =
    !options?.requireText ||
    typed.trim().toLowerCase() === options.requireText.trim().toLowerCase();

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options ? (
        <Modal
          open
          onClose={() => close(false)}
          title={options.title}
          initialFocusRef={cancelRef as React.RefObject<HTMLElement>}
          footer={
            <div className="lks-modal__actions">
              <button
                ref={cancelRef}
                type="button"
                className="btn btn-outline-secondary"
                data-lks-autofocus
                onClick={() => close(false)}
              >
                {options.cancelLabel ?? "Zrušit"}
              </button>
              <button
                type="button"
                className={
                  options.tone === "danger" ? "btn btn-danger" : "btn btn-primary"
                }
                disabled={!requireOk}
                onClick={() => close(true)}
              >
                {options.confirmLabel ?? "Potvrdit"}
              </button>
            </div>
          }
        >
          <div className="lks-confirm__message">{options.message}</div>
          {options.details ? (
            <div className="lks-confirm__details">{options.details}</div>
          ) : null}
          {options.requireText ? (
            <div className="lks-confirm__require">
              <label htmlFor="lks-confirm-require">
                Pro potvrzení opište: <strong>{options.requireText}</strong>
              </label>
              <input
                id="lks-confirm-require"
                className="form-control"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            </div>
          ) : null}
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
};

/**
 * Potvrzovací dialog: const confirm = useConfirm();
 * `await confirm({...})` vrací true/false. Mimo provider vrací vždy false
 * (a zaloguje) – bezpečné selhání místo pádu.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return async () => {
      console.error("[LKS] useConfirm volán mimo ConfirmProvider");
      return false;
    };
  }
  return ctx;
}

export default ConfirmProvider;
